import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, BehaviorSubject, interval, Subscription } from 'rxjs';
import { map, switchMap, take, shareReplay, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { 
  faCopy, 
  faCheckCircle, 
  faExclamationTriangle,
  faPen,
  faCalendar,
  faChevronDown,
  faChevronUp,
  faUserXmark
} from '@fortawesome/free-solid-svg-icons';

import { environment } from 'src/environments/environment';
import { OrderSearchService } from '../manage-orders/services/order-search.service';
import { OrderSearchModel } from '../manage-orders/models/order-search.models';
import { ParticipantSummary } from '../shared/models/orderSearchList';
import { DateHelper } from '../shared/helpers/date.helper';
import { AppSpinnerModule } from '../shared/modules/app-spinner.module';
import { OrderSummary, getDateRange, calculateOrderSummary } from './order-summary.utils';
import { AuditLogModalComponent } from '../shared/components/audit-log-modal/audit-log-modal.component';

export type TileType = 'ready' | 'active' | 'complete' | 'cancelled' | 'unsuccessful';

export interface SimpleOrderDetail {
  orderId: number;
  loanIdentifier: string;
  participants: ParticipantSummary[];
  clientName: string;
  scheduledDateTime: string;
  statusCode: string;
  statusUpdatedDate: string;
  orderType: string;
  thirdPartyOrderId: string;
  packageGuid: string;
}

export interface InProgressOrderDetail extends SimpleOrderDetail {
  signingAgentEmail: string | null;
}

@UntilDestroy()
@Component({
  selector: 'app-todays-orders-report',
  templateUrl: './todays-orders-report.component.html',
  styleUrls: ['./todays-orders-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TodaysOrdersReportComponent implements OnInit {
  faCopy = faCopy;
  faCheckCircle = faCheckCircle;
  faExclamationTriangle = faExclamationTriangle;
  faPen = faPen;
  faCalendar = faCalendar;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faUserXmark = faUserXmark;
  
  selectedDate = new Date();
  dateControl = new FormControl<Date | null>(this.selectedDate);
  selectedTile: TileType = 'active';
  
  @ViewChild('datePicker') datePicker: MatDatepicker<Date>;
  
  expandedOrders = new Set<number>();
  autoRefreshEnabled = false;
  isCopyingEmails = false;

  tableFilters = {
    orderId: '',
    loanId: '',
    thirdPartyId: '',
    packageGuid: '',
    orderType: [] as string[]
  };

  orderTypeOptions = this.buildOrderTypeOptions();

  private buildOrderTypeOptions(): { key: string; label: string }[] {
    const options = [
      { key: 'Hybrid', label: 'Hybrid' },
      { key: 'KRON', label: 'KRON' },
      { key: 'RON', label: 'RON' },
      { key: 'IPEN', label: 'IPEN' },
    ];
    if (environment.features.isESignEnabled === 'true') {
      options.push({ key: 'eSign', label: 'eSign' });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }
  
  private refreshSubject = new BehaviorSubject<void>(undefined);
  private autoRefreshSubscription?: Subscription;
  
  orderSummary$: Observable<OrderSummary>;
  inProgressOrders$: Observable<InProgressOrderDetail[]>;
  readyOrders$: Observable<SimpleOrderDetail[]>;
  completeOrders$: Observable<SimpleOrderDetail[]>;
  cancelledOrders$: Observable<SimpleOrderDetail[]>;
  unsuccessfulOrders$: Observable<SimpleOrderDetail[]>;
  private allOrders$: Observable<{ totalRows: number; results: any[] }>;
  
  constructor(
    private orderSearchService: OrderSearchService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private dateHelper: DateHelper,
    private spinner: AppSpinnerModule,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.initializeData();
  }

  toggleOrderExpansion(orderId: number): void {
    if (this.expandedOrders.has(orderId)) {
      this.expandedOrders.delete(orderId);
    } else {
      this.expandedOrders.add(orderId);
    }
  }

  isOrderExpanded(orderId: number): boolean {
    return this.expandedOrders.has(orderId);
  }

  private initializeData(): void {
    this.spinner.show();
    this.expandedOrders.clear();
    const refreshTrigger$ = this.refreshSubject.asObservable();
    
    this.allOrders$ = refreshTrigger$.pipe(
      switchMap(() => this.orderSearchService.search(this.createOrdersSearchParams())),
      shareReplay(1),
      untilDestroyed(this)
    );

    this.orderSummary$ = this.allOrders$.pipe(
      map(results => calculateOrderSummary(results.results)),
      untilDestroyed(this)
    );

    this.readyOrders$ = this.allOrders$.pipe(
      map(results => {
        return results.results
          .filter(order => order.order.statusCode === 'READY')
          .map(order => this.mapToSimpleOrder(order));
      }),
      untilDestroyed(this)
    );

    this.completeOrders$ = this.allOrders$.pipe(
      map(results => {
        return results.results
          .filter(order => order.order.statusCode === 'COMPLETE')
          .map(order => this.mapToSimpleOrder(order));
      }),
      untilDestroyed(this)
    );

    this.cancelledOrders$ = this.allOrders$.pipe(
      map(results => {
        return results.results
          .filter(order => order.order.statusCode === 'CANCELLED')
          .map(order => this.mapToSimpleOrder(order));
      }),
      untilDestroyed(this)
    );

    this.unsuccessfulOrders$ = this.allOrders$.pipe(
      map(results => {
        const unsuccessfulStatuses = ['SIGNINGFAILED', 'OPTOUT', 'VAULTINGFAILED'];
        return results.results
          .filter(order => unsuccessfulStatuses.includes(order.order.statusCode))
          .map(order => this.mapToSimpleOrder(order));
      }),
      untilDestroyed(this)
    );

    this.inProgressOrders$ = this.allOrders$.pipe(
      map(results => {
        const includedStatuses = ['INPROGRESS', 'SIGNING', 'SESSIONCOMPLETE', 'VAULTING'];
        return results.results
          .filter(order => includedStatuses.includes(order.order.statusCode))
          .map(order => {
            const simpleOrder = this.mapToSimpleOrder(order);
            const signingAgent = simpleOrder.participants.find(p => p.role === 'SIGNINGAGENT');
            return {
              ...simpleOrder,
              signingAgentEmail: signingAgent?.email || null
            } as InProgressOrderDetail;
          });
      }),
      tap(() => this.spinner.hide()),
      shareReplay(1),
      untilDestroyed(this)
    );
  }

  private mapToSimpleOrder(order: any): SimpleOrderDetail {
    return {
      orderId: order.order.id,
      loanIdentifier: order.order.loanIdentifier,
      participants: order.participants || [],
      clientName: order.clientName,
      scheduledDateTime: order.order.scheduledDateTime.toString(),
      statusCode: order.order.statusCode,
      statusUpdatedDate: order.order.updatedDate ? order.order.updatedDate.toString() : '',
      orderType: this.getDisplayProductType(order.order.productType),
      thirdPartyOrderId: order.order.thirdPartyOrderIdentifier || 'N/A',
      packageGuid: order.order.packageGuid || ''
    };
  }

  selectTile(tile: TileType): void {
    this.selectedTile = tile;
    this.expandedOrders.clear();
    this.cdr.detectChanges();
  }

  filterOrders<T extends SimpleOrderDetail>(orders: T[]): T[] {
    const { orderId, loanId, thirdPartyId, packageGuid, orderType } = this.tableFilters;
    if (!orderId && !loanId && !thirdPartyId && !packageGuid && !orderType.length) {
      return orders;
    }
    return orders.filter(order => {
      if (orderId && !order.orderId.toString().includes(orderId.trim())) return false;
      if (loanId && !(order.loanIdentifier || '').toLowerCase().includes(loanId.trim().toLowerCase())) return false;
      if (thirdPartyId && !(order.thirdPartyOrderId || '').toLowerCase().includes(thirdPartyId.trim().toLowerCase())) return false;
      if (packageGuid && !(order.packageGuid || '').toLowerCase().includes(packageGuid.trim().toLowerCase())) return false;
      if (orderType.length && !orderType.includes(order.orderType || '')) return false;
      return true;
    });
  }

  hasActiveFilters(): boolean {
    const { orderId, loanId, thirdPartyId, packageGuid, orderType } = this.tableFilters;
    return !!orderId || !!loanId || !!thirdPartyId || !!packageGuid || orderType.length > 0;
  }

  onFilterChange(): void {
    this.cdr.markForCheck();
  }

  clearFilters(): void {
    this.tableFilters = { orderId: '', loanId: '', thirdPartyId: '', packageGuid: '', orderType: [] };
    this.cdr.markForCheck();
  }

  onDateSelected(event: MatDatepickerInputEvent<Date>): void {
    if (event.value) {
      this.spinner.show();
      this.selectedDate = event.value;
      this.datePicker.close();
      this.dateControl.setValue(this.selectedDate);
      this.selectedTile = 'active';
      
      if (!this.isToday()) {
        this.autoRefreshEnabled = false;
        this.stopAutoRefresh();
      }
      
      this.refreshSubject.next();
    }
  }

  getFormattedSelectedDate(): string {
    return this.selectedDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  isToday(): boolean {
    const today = new Date();
    return this.selectedDate.toDateString() === today.toDateString();
  }

  private getSigningAgentEmailsFromOrders(orders: any[]): string[] {
    return orders
      .map(order => {
        const signingAgent = (order.participants || []).find((p: ParticipantSummary) => p.role === 'SIGNINGAGENT');
        return signingAgent?.email;
      })
      .filter((email): email is string => !!email);
  }

  private createOrdersSearchParams(): OrderSearchModel {
    const { startOfDay, endOfDay } = getDateRange(this.selectedDate);

    return {
      clientId: 0,
      startRow: 1,
      maxRow: 100000,
      scheduledDateTimeStart: startOfDay.toISOString(),
      scheduledDateTimeEnd: endOfDay.toISOString(),
      isSortOrderAscending: false,
      sortByColumn: 'ScheduledDateTime',
      showOnly: []
    };
  }

  private onAutoRefresh(): void {
    this.refreshSubject.next();
  }

  toggleAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    
    this.autoRefreshSubscription = interval(60000)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.onAutoRefresh();
      });
  }

  private stopAutoRefresh(): void {
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
      this.autoRefreshSubscription = undefined;
    }
  }

  onCopySigningAgentEmails(): void {
    this.isCopyingEmails = true;
    
    this.allOrders$.pipe(
      map(results => {
        const relevantStatuses = ['READY', 'INPROGRESS', 'SIGNING', 'SESSIONCOMPLETE', 'VAULTING'];
        const relevantOrders = results.results.filter((order: any) => 
          relevantStatuses.includes(order.order.statusCode)
        );
        const allEmails = this.getSigningAgentEmailsFromOrders(relevantOrders);
        return [...new Set(allEmails)];
      }),
      take(1)
    ).subscribe({
      next: (signingAgentEmails) => {
        const emailString = signingAgentEmails.join('; ');

        if (emailString) {
          navigator.clipboard.writeText(emailString).then(() => {
            this.isCopyingEmails = false;
            this.cdr.detectChanges();
            this.toastr.success(`Successfully copied signing agent emails to clipboard!`);
          }).catch(() => {
            const textArea = document.createElement('textarea');
            textArea.value = emailString;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            this.isCopyingEmails = false;
            this.cdr.detectChanges();
            this.toastr.success(`Successfully copied signing agent emails to clipboard!`);
          });
        } else {
          this.isCopyingEmails = false;
          this.cdr.detectChanges();
          this.toastr.info('No signing agent emails found for ready or active orders.');
        }
      },
      error: (error) => {
        console.error('Error copying signing agent emails:', error);
        this.isCopyingEmails = false;
        this.cdr.detectChanges();
        this.toastr.error('Failed to copy signing agent emails.');
      }
    });
  }

  formatDateTime(dateTime: string): string {
    const convertedDate = this.dateHelper.convertDateTimeToLocalDateTime(dateTime);
    return convertedDate.toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  formatUpdatedDateTime(dateTime: string): string {
    const timezoneMatch = dateTime.match(/([+-])(\d{2}):(\d{2})$/);
    if (timezoneMatch) {
      const sign = timezoneMatch[1] === '-' ? -1 : 1;
      const offsetHours = parseInt(timezoneMatch[2]);
      const offsetMinutes = parseInt(timezoneMatch[3]);

      const date = new Date(dateTime);
      date.setHours(date.getHours() + (sign * offsetHours));
      date.setMinutes(date.getMinutes() + (sign * offsetMinutes));
      date.setSeconds(0);
      date.setMilliseconds(0);

      return date.toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }

    const date = new Date(dateTime);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date.toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  getStepCompleted(stepStatus: string, currentStatus: string): boolean {
    const statusOrder = ['READY', 'INPROGRESS', 'SIGNING', 'SESSIONCOMPLETE', 'VAULTING', 'COMPLETE'];
    const stepIndex = statusOrder.indexOf(stepStatus);
    const currentIndex = statusOrder.indexOf(currentStatus);
    
    if (currentIndex === -1) {
      return stepIndex <= 1;
    }
    
    return currentIndex > stepIndex;
  }

  getDisplayProductType(productType: string): string {
    if (!productType) {
      return 'N/A';
    }
    
    switch (productType) {
      case 'RemoteSigning':
        return 'RON';
      case 'IPEN':
        return 'IPEN';
      case 'Hybrid':
        return 'Hybrid';
      case 'KnownSignerRON':
        return 'KRON';
      case 'eSign':
        return 'eSign';
      default:
        return productType;
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toastr.success('Successfully copied to clipboard.');
    });
  }

  openAuditLogModal(order: SimpleOrderDetail): void {
    const modalRef = this.modalService.open(AuditLogModalComponent, {
      centered: true,
      size: 'xl'
    });
    modalRef.componentInstance.orderId = order.orderId;
  }
}
